'use client';

import classNames from 'classnames';
import {
  Options,
  Splide,
  SplideSlide,
} from '@splidejs/react-splide';
import {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { SliderItem } from '@sapientpro/sapientpro-data-models';
import { Slider, SliderVideo } from '@sapientpro/sapientpro-data-models/src/components';
import { useAtomValue } from 'jotai';
import styles from './SuccessIndicators.module.scss';
import SuccessIndicatorItem from './SuccessIndicatorItem';
import SliderComponent from '../SliderComponent/Slider';
import SuccessIndicatorsVideoItem from './SuccessIndicatorsVideoItem';
import Button, { ButtonVariants } from '../Button';
import deviceScreenSize, { ScreenSize } from '../../store/deviceScreenSize';
import AnimatedTitle from '../AnimatedTitle';
import VideoBlock from './VideoBlock';

const settings: Options = {
  focus: 0,
  perPage: 1,
  perMove: 1,
  pagination: false,
  arrows: true,
  autoWidth: true,
  gap: '20px',
  padding: { left: '320px' },
  type: 'loop',
  breakpoints: {
    767: {
      padding: { left: '24px' },
    },
    1023: {
      padding: { left: '64px' },
    },
    1279: {
      padding: { left: '72px' },
    },
    1439: {
      padding: { left: '120px' },
    },
    1919: {
      padding: { left: '200px' },
    },
  },
  classes: {
    pagination: `splide__pagination ${styles.pagination} container`,
    page: `splide__pagination__page ${styles.pagination__item}`,
  },
};

type TSliderItem = SliderItem & {
  video?: SliderVideo;
};

const SuccessIndicatorsSection = ({ data }: { data: Slider }) => {
  const screenSize = useAtomValue(deviceScreenSize);
  const [showVideo, setShowVideo] = useState<boolean>(false);
  const videoBlockRef = useRef<HTMLDivElement>(null);
  const sliderRef = useRef<Splide>(null);

  const sliderItems:TSliderItem[] = useMemo(() => {
    const resultItems:TSliderItem[] = [...data.items];
    const { id, title, subtitle } = data.video as SliderVideo;
    resultItems.splice(data.positionNumber, 0, {
      id, title, subtitle, video: data.video,
    });

    return resultItems;
  }, [data.positionNumber, data.video]);

  const isMobile = screenSize < ScreenSize.TABLET_PORTRAIT;

  useEffect(() => {
    if (videoBlockRef.current && !isMobile) {
      const videoRect = videoBlockRef.current.getBoundingClientRect();
      const scrollPosition = window.scrollY;

      const newScrollPosition = scrollPosition + videoRect.y - videoRect.height;

      window.scrollTo({ top: newScrollPosition, behavior: 'smooth' });
    }
  }, [videoBlockRef.current, showVideo]);

  const displayVideoBlock = showVideo || isMobile;
  const mobileTitle = `**${data?.video?.title}** ${data?.video?.subtitle}`;
  const title = isMobile ? mobileTitle : data.title;

  return (
    <section className={classNames(styles.successIndicatorsSection)}>
      <h2 className={classNames(styles.title, 'container', 'home-page')}>
        <p className={styles.titleText}>
          <AnimatedTitle title={title || data.title} />
        </p>
        <Button
          variant={ButtonVariants.TRANSPARENT}
          link='/cases'
          className={styles.titleButton}
          icon={(
            <svg>
              <use
                xlinkHref='/media/icon-nest.svg#iconNest'
                href='/media/icon-nest.svg#iconNest'
              />
            </svg>
          )}
        >
          View All
        </Button>
      </h2>
      <div className={styles.sliderWrapper}>
        <SliderComponent
          settings={settings}
          classNames={{
            navigation: `${styles.arrows} container home-page`,
            arrow: styles.arrow,
            prevArrow: styles.arrow_prev,
            nextArrow: styles.arrow_next,
            slider: styles.slider,
          }}
          ref={sliderRef}
        >
          {sliderItems.map((item, index) => {
            const isAfterMedia = index > data.positionNumber;
            const shift = isAfterMedia ? 0 : 1;
            const isHighlighted = !Math.abs((index + shift) % 4);

            return (
              <SplideSlide key={item.id}>
                {item.video ? (
                  <SuccessIndicatorsVideoItem
                    title={item.title}
                    text={item.subtitle}
                    videoPreview={item?.video?.previewImage?.url}
                    showVideo={() => { setShowVideo(true); }}
                  />
                ) : (
                  <SuccessIndicatorItem
                    key={item.id}
                    title={item.title}
                    text={item.subtitle}
                    isHighlighted={isHighlighted}
                  />
                )}
              </SplideSlide>
            );
          })}
        </SliderComponent>
      </div>
      {displayVideoBlock && (
      <div
        className={classNames(styles.videoBlock, 'container', 'home-page')}
        ref={videoBlockRef}
      >
        <VideoBlock
          isMobile={isMobile}
          videoUrl={data.video?.video?.url}
        />
      </div>
      )}
    </section>
  );
};

export default SuccessIndicatorsSection;
